import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import {
  Tag, Search, Calendar, Building2, Package, CheckCircle,
  XCircle, Clock, Filter, ChevronRight, ChevronLeft, DollarSign, Percent,
  QrCode, TrendingUp, X, ExternalLink, Star, FileText,
  AlertCircle, Loader2, Eye, CheckSquare, Copy, Download,
  Plus, Save, History, Edit2, Video, Link, Power, PowerOff, AlertTriangle, RefreshCw, Trash2,
} from 'lucide-react';
import { toast } from 'react-toastify';
import {
  GET_ALL_PROMOTIONS, GET_PROMOTION_LABELS, GET_LABELS_STATISTICS,
  GENERATE_QR_CODES, GENERATE_DRAFT_LABELS_EXCEL, DELETE_DRAFT_LABELS,
  UPDATE_PROMOTION, UPDATE_PRODUCT, GET_PRODUCT_SUBCATEGORIES, CONFIRM_LABELS,
} from '../../api/laboratory-api';
import { getProductImageUrl, uploadProductVideo } from '../../utils/imageUrl';
import { PRODUCT_CATEGORIES, getCategoryLabel, getCategoryIcon } from '../../constants/product-categories';

// ─── helpers ─────────────────────────────────────────────────────────────────

const isActive  = (p) => {
  const now = new Date();
  return p.status === 'IN_PROGRESS' && new Date(p.startDate) <= now && new Date(p.endDate) >= now;
};
const isExpired = (p) => new Date(p.endDate) < new Date();

const getMazidDH = (promo) => {
  const pct  = parseFloat(promo.commissionPercentage);
  const pght = promo.products?.[0]?.prixPGHT;
  if (!pct || !pght) return null;
  return (pght * pct / 100).toFixed(2);
};

const getPromoTag = (p) => {
  if (isExpired(p))         return { label: 'Expirée',  bg: 'bg-red-100',    text: 'text-red-700',    dot: 'bg-red-400'    };
  if (isActive(p))          return { label: 'Active',   bg: 'bg-green-100',  text: 'text-green-700',  dot: 'bg-green-500'  };
  if (p.status === 'DRAFT') return { label: 'Brouillon',bg: 'bg-yellow-100', text: 'text-yellow-700', dot: 'bg-yellow-400' };
  return                           { label: 'En cours', bg: 'bg-blue-100',   text: 'text-blue-700',   dot: 'bg-blue-400'   };
};

// ─── download helpers ─────────────────────────────────────────────────────────

const downloadPDF = (base64, filename) => {
  try {
    const bytes = atob(base64);
    const arr = new Uint8Array(bytes.length);
    for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
    const blob = new Blob([arr], { type: 'application/pdf' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url; a.download = filename || `qr_${Date.now()}.pdf`;
    document.body.appendChild(a); a.click();
    document.body.removeChild(a); URL.revokeObjectURL(url);
  } catch (e) { toast.error(`Erreur PDF: ${e.message}`); }
};

const downloadExcel = (base64, filename) => {
  try {
    const bytes = atob(base64);
    const arr = new Uint8Array(bytes.length);
    for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
    const blob = new Blob([arr], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url; a.download = filename || `qr_${Date.now()}.xlsx`;
    document.body.appendChild(a); a.click();
    document.body.removeChild(a); URL.revokeObjectURL(url);
  } catch (e) { toast.error(`Erreur Excel: ${e.message}`); }
};

const downloadQRImage = (label) => {
  const a = document.createElement('a');
  a.href = label.image;
  a.download = `qr-${label.code}.png`;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
};

// ─── Product Detail Side Panel ────────────────────────────────────────────────

const ProductDetailPanel = ({ product, lab, onClose, onProductUpdated }) => {
  const [imgIdx, setImgIdx]         = useState(0);
  const [isEditing, setIsEditing]   = useState(false);
  const [saving, setSaving]         = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [editForm, setEditForm]     = useState(null);
  const [localProduct, setLocalProduct] = useState(product);

  const { data: subcatData } = useQuery(GET_PRODUCT_SUBCATEGORIES, { fetchPolicy: 'cache-and-network' });
  const allSubcats = subcatData?.productSubcategories ?? [];

  const [updateProduct] = useMutation(UPDATE_PRODUCT, {
    onError: (err) => { toast.error(`Erreur: ${err.message}`); setSaving(false); },
  });

  // Sync localProduct when prop changes (e.g. parent refetch)
  React.useEffect(() => { if (product) setLocalProduct(product); }, [product]);

  const images = localProduct
    ? [localProduct.image, localProduct.images, localProduct.imagethree, localProduct.quatriemimage, localProduct.cinquiemimage].filter(Boolean)
    : [];

  const availableSubcats = editForm?.category
    ? allSubcats.filter(s => s.category === editForm.category).map(s => s.name)
    : [];

  const startEdit = () => {
    setEditForm({
      name:               localProduct.name        || '',
      price:              localProduct.price       ?? '',
      prixPGHT:           localProduct.prixPGHT    ?? '',
      category:           localProduct.category    || '',
      subcategory:        localProduct.subcategory || '',
      description:        localProduct.description || '',
      videoLink:          localProduct.videoLink   || '',
      videoFile:          null,
      videoFileName:      '',
      imagePreview:       localProduct.image       || null,
      additionalPreviews: [
        localProduct.images        || null,
        localProduct.imagethree    || null,
        localProduct.quatriemimage || null,
        localProduct.cinquiemimage || null,
      ],
    });
    setIsEditing(true);
    setImgIdx(0);
  };

  const handleImageFile = (file, isMain, idx) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('Image max 5 MB'); return; }
    if (!file.type.startsWith('image/')) { toast.error('Fichier image invalide'); return; }
    const reader = new FileReader();
    reader.onloadend = () => {
      if (isMain) {
        setEditForm(f => ({ ...f, imagePreview: reader.result }));
      } else {
        setEditForm(f => {
          const ap = [...f.additionalPreviews];
          ap[idx] = reader.result;
          return { ...f, additionalPreviews: ap };
        });
      }
    };
    reader.readAsDataURL(file);
  };

  const handleVideoFile = async (file) => {
    if (!file) return;
    if (file.size > 200 * 1024 * 1024) { toast.error('Vidéo max 200 MB'); return; }
    setEditForm(f => ({ ...f, videoFile: file, videoFileName: file.name, videoLink: '' }));
  };

  const handleSave = async () => {
    if (!editForm.name.trim()) { toast.warning('Le nom est requis'); return; }
    setSaving(true);
    try {
      let videoPath = localProduct.video || undefined;
      if (editForm.videoFile) {
        setUploadingVideo(true);
        try {
          videoPath = await uploadProductVideo(editForm.videoFile);
        } catch (err) {
          toast.error(`Erreur upload vidéo : ${err.message}`);
          setSaving(false); setUploadingVideo(false); return;
        }
        setUploadingVideo(false);
      }
      const result = await updateProduct({
        variables: {
          updateProductInput: {
            id:           localProduct.id,
            name:         editForm.name.trim(),
            price:        editForm.price !== '' ? parseFloat(editForm.price) : undefined,
            prixPGHT:     editForm.prixPGHT !== '' ? parseFloat(editForm.prixPGHT) : undefined,
            description:  editForm.description || undefined,
            category:     editForm.category    || undefined,
            subcategory:  editForm.subcategory || undefined,
            videoLink:    editForm.videoLink   || undefined,
            video:        videoPath,
            image:        editForm.imagePreview       || undefined,
            images:       editForm.additionalPreviews[0] || undefined,
            imagethree:   editForm.additionalPreviews[1] || undefined,
            quatriemimage:   editForm.additionalPreviews[2] || undefined,
            cinquiemimage:   editForm.additionalPreviews[3] || undefined,
          },
        },
      });
      toast.success('Produit mis à jour');
      const updated = result.data.updateProduct;
      setLocalProduct(updated);
      setIsEditing(false);
      if (onProductUpdated) onProductUpdated(updated);
    } catch { /* handled by onError */ }
    finally { setSaving(false); setUploadingVideo(false); }
  };

  if (!localProduct) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex">
      <div className="flex-1 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="w-full max-w-md bg-white h-full shadow-2xl flex flex-col overflow-hidden">

        {/* Header */}
        <div className="px-6 py-5 border-b bg-gray-50 border-gray-100">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-10 h-10 bg-indigo-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <Package size={18} className="text-indigo-600" />
              </div>
              <div className="min-w-0">
                <h2 className="text-base font-bold text-gray-900 truncate">{localProduct.name}</h2>
                {lab && <p className="text-xs text-gray-500 mt-0.5">{lab.nom}</p>}
              </div>
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              {!isEditing && (
                <button
                  onClick={startEdit}
                  className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-indigo-700 bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 rounded-lg transition-colors"
                >
                  <Edit2 size={12} /> Modifier
                </button>
              )}
              <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-200 transition-colors ml-1">
                <X size={18} className="text-gray-500" />
              </button>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-5">

          {/* Image carousel (view) or image editor */}
          <section>
            {isEditing ? (
              <div className="space-y-3">
                {/* Main image */}
                <div className="relative group">
                  {editForm.imagePreview ? (
                    <>
                      <img
                        src={getProductImageUrl(editForm.imagePreview)}
                        alt="principale"
                        className="w-full h-44 object-cover rounded-xl border border-gray-200"
                      />
                      <button
                        onClick={() => setEditForm(f => ({ ...f, imagePreview: null }))}
                        className="absolute top-1.5 right-1.5 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600"
                      >
                        <X size={12} />
                      </button>
                    </>
                  ) : (
                    <label className="flex flex-col items-center justify-center w-full h-44 bg-gray-50 border-2 border-dashed border-gray-300 rounded-xl cursor-pointer hover:border-indigo-400 transition-colors">
                      <Plus size={20} className="text-gray-400 mb-1" />
                      <span className="text-xs text-gray-400">Image principale</span>
                      <input type="file" accept="image/*" className="hidden" onChange={e => handleImageFile(e.target.files[0], true)} />
                    </label>
                  )}
                  {editForm.imagePreview && (
                    <label className="absolute bottom-1.5 right-1.5 px-2 py-1 bg-white/90 text-xs text-indigo-700 border border-indigo-200 rounded-lg cursor-pointer hover:bg-indigo-50 flex items-center gap-1">
                      <Plus size={11} /> Changer
                      <input type="file" accept="image/*" className="hidden" onChange={e => handleImageFile(e.target.files[0], true)} />
                    </label>
                  )}
                </div>
                {/* Additional images */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {[0, 1, 2, 3].map(i => (
                    <div key={i} className="relative">
                      {editForm.additionalPreviews[i] ? (
                        <>
                          <img
                            src={getProductImageUrl(editForm.additionalPreviews[i])}
                            alt={`img ${i + 2}`}
                            className="w-full h-16 object-cover rounded-lg border border-gray-200"
                          />
                          <button
                            onClick={() => {
                              setEditForm(f => {
                                const ap = [...f.additionalPreviews]; ap[i] = null;
                                return { ...f, additionalPreviews: ap };
                              });
                            }}
                            className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white rounded-full flex items-center justify-center"
                          >
                            <X size={9} />
                          </button>
                        </>
                      ) : (
                        <label className="flex items-center justify-center w-full h-16 bg-gray-50 border-2 border-dashed border-gray-200 rounded-lg cursor-pointer hover:border-indigo-300 transition-colors">
                          <Plus size={14} className="text-gray-400" />
                          <input type="file" accept="image/*" className="hidden" onChange={e => handleImageFile(e.target.files[0], false, i)} />
                        </label>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ) : images.length > 0 ? (
              <div className="relative group">
                <img
                  src={getProductImageUrl(images[imgIdx])}
                  alt={localProduct.name}
                  className="w-full h-52 object-cover rounded-xl border border-gray-200"
                />
                {images.length > 1 && (
                  <>
                    <button
                      onClick={() => setImgIdx(i => (i - 1 + images.length) % images.length)}
                      className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-white/80 hover:bg-white rounded-full shadow flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <ChevronLeft size={16} className="text-gray-700" />
                    </button>
                    <button
                      onClick={() => setImgIdx(i => (i + 1) % images.length)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-white/80 hover:bg-white rounded-full shadow flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <ChevronRight size={16} className="text-gray-700" />
                    </button>
                    <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1">
                      {images.map((_, i) => (
                        <button key={i} onClick={() => setImgIdx(i)}
                          className={`w-1.5 h-1.5 rounded-full transition-colors ${i === imgIdx ? 'bg-white' : 'bg-white/50'}`}
                        />
                      ))}
                    </div>
                  </>
                )}
              </div>
            ) : (
              <div className="w-full h-52 bg-gray-100 rounded-xl border border-gray-200 flex items-center justify-center">
                <Package size={40} className="text-gray-300" />
              </div>
            )}
          </section>

          {/* Edit form OR view mode */}
          {isEditing ? (
            <section className="space-y-3">
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Informations</h3>

              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Nom *</label>
                <input type="text" value={editForm.name}
                  onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-400"
                />
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1 block">Prix public (DH)</label>
                  <input type="number" step="0.01" value={editForm.price}
                    onChange={e => setEditForm(f => ({ ...f, price: e.target.value }))}
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-400"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1 block">Prix PGHT (DH)</label>
                  <input type="number" step="0.01" value={editForm.prixPGHT}
                    onChange={e => setEditForm(f => ({ ...f, prixPGHT: e.target.value }))}
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-400"
                  />
                </div>
              </div>

              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Catégorie</label>
                <select value={editForm.category}
                  onChange={e => setEditForm(f => ({ ...f, category: e.target.value, subcategory: '' }))}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-400"
                >
                  <option value="">— Sélectionner —</option>
                  {PRODUCT_CATEGORIES.map(c => (
                    <option key={c.key} value={c.key}>{c.icon} {c.label}</option>
                  ))}
                </select>
              </div>

              {editForm.category && (
                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1 block">Sous-catégorie</label>
                  {availableSubcats.length > 0 ? (
                    <select value={editForm.subcategory}
                      onChange={e => setEditForm(f => ({ ...f, subcategory: e.target.value }))}
                      className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-400"
                    >
                      <option value="">— Aucune —</option>
                      {availableSubcats.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  ) : (
                    <p className="text-xs text-gray-400 italic">Aucune sous-catégorie pour cette catégorie.</p>
                  )}
                </div>
              )}

              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Description</label>
                <textarea rows={3} value={editForm.description}
                  onChange={e => setEditForm(f => ({ ...f, description: e.target.value }))}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-400 resize-none"
                />
              </div>

              {/* Video */}
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block flex items-center gap-1">
                  <Video size={11} /> Vidéo
                </label>
                {editForm.videoFileName ? (
                  <div className="flex items-center gap-2 px-3 py-2 bg-green-50 border border-green-200 rounded-lg text-xs text-green-800">
                    <Video size={13} className="flex-shrink-0" />
                    <span className="flex-1 truncate">{editForm.videoFileName}</span>
                    <button onClick={() => setEditForm(f => ({ ...f, videoFile: null, videoFileName: '' }))}
                      className="text-red-500 hover:text-red-700 flex-shrink-0"><X size={13} /></button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <input type="url" placeholder="https://youtube.com/…" value={editForm.videoLink}
                      onChange={e => setEditForm(f => ({ ...f, videoLink: e.target.value }))}
                      className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-400"
                    />
                    <label className="flex items-center gap-1 px-2.5 py-2 text-xs font-medium text-gray-600 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 whitespace-nowrap">
                      <Plus size={12} /> Fichier
                      <input type="file" accept="video/mp4,video/quicktime,video/webm" className="hidden"
                        onChange={e => handleVideoFile(e.target.files[0])} />
                    </label>
                  </div>
                )}
              </div>

              <div className="flex gap-2 pt-1">
                <button onClick={handleSave} disabled={saving}
                  className="flex-1 flex items-center justify-center gap-1.5 py-2 text-sm font-medium bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving
                    ? <><div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        {uploadingVideo ? 'Upload vidéo…' : 'Enregistrement…'}</>
                    : <><Save size={13} /> Enregistrer</>
                  }
                </button>
                <button onClick={() => setIsEditing(false)} disabled={saving}
                  className="px-4 py-2 text-sm font-medium border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                >
                  Annuler
                </button>
              </div>
            </section>
          ) : (
            <>
              <section className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-green-50 rounded-xl border border-green-100">
                  <p className="text-xs text-green-600 font-medium mb-1">Prix public</p>
                  <p className="text-lg font-bold text-green-800">
                    {localProduct.price != null ? `${localProduct.price.toFixed(2)} DH` : '—'}
                  </p>
                </div>
                <div className="p-3 bg-blue-50 rounded-xl border border-blue-100">
                  <p className="text-xs text-blue-600 font-medium mb-1">Prix PGHT</p>
                  <p className="text-lg font-bold text-blue-800">
                    {localProduct.prixPGHT != null ? `${localProduct.prixPGHT.toFixed(2)} DH` : '—'}
                  </p>
                </div>
              </section>

              {localProduct.category && (
                <section>
                  <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Catégorie</h3>
                  <div className="flex flex-wrap gap-2">
                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-indigo-50 border border-indigo-100 text-indigo-700 rounded-full text-sm font-medium">
                      {getCategoryIcon(localProduct.category)} {getCategoryLabel(localProduct.category)}
                    </span>
                    {localProduct.subcategory && (
                      <span className="inline-flex items-center px-2.5 py-1.5 bg-gray-100 text-gray-600 rounded-full text-xs font-medium">
                        {localProduct.subcategory}
                      </span>
                    )}
                  </div>
                </section>
              )}

              {localProduct.description && (
                <section>
                  <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Description</h3>
                  <p className="text-sm text-gray-700 leading-relaxed bg-gray-50 rounded-xl p-4 border border-gray-100">
                    {localProduct.description}
                  </p>
                </section>
              )}

              {localProduct.videoLink && (
                <section>
                  <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                    <Video size={12} /> Vidéo
                  </h3>
                  <a href={localProduct.videoLink} target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-2 p-3 bg-red-50 border border-red-100 rounded-xl text-sm text-red-700 hover:bg-red-100 transition-colors"
                  >
                    <Video size={14} className="flex-shrink-0" />
                    <span className="truncate flex-1 text-xs">{localProduct.videoLink}</span>
                    <Link size={12} className="flex-shrink-0" />
                  </a>
                </section>
              )}

              {lab && (
                <section>
                  <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Laboratoire</h3>
                  <div className="flex items-center gap-3 p-3 bg-white border border-gray-200 rounded-xl">
                    <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Building2 size={14} className="text-indigo-600" />
                    </div>
                    <span className="text-sm font-semibold text-gray-900">{lab.nom}</span>
                  </div>
                </section>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── QR Detail Side Panel ─────────────────────────────────────────────────────

const QRDetailPanel = ({ promotion, onClose, onRefetch }) => {
  const [qrSearch, setQrSearch]   = useState('');
  const [qrFilter, setQrFilter]   = useState('all');
  const [newQrQty, setNewQrQty]   = useState(100);
  const [generating, setGenerating] = useState(false);
  const [exportingDraft, setExportingDraft] = useState(false);
  const [showDeleteDraftConfirm, setShowDeleteDraftConfirm] = useState(false);
  const [deletingDraft, setDeletingDraft] = useState(false);

  const { data: labelsData, loading: labelsLoading, refetch: refetchLabels } = useQuery(GET_PROMOTION_LABELS, {
    variables: { promotionId: promotion?.id || '' },
    skip: !promotion?.id,
    fetchPolicy: 'cache-and-network',
  });

  const { data: statsData, refetch: refetchStats } = useQuery(GET_LABELS_STATISTICS, {
    variables: { promotionId: promotion?.id || '' },
    skip: !promotion?.id,
    fetchPolicy: 'cache-and-network',
  });

  const [generateQRCodes] = useMutation(GENERATE_QR_CODES, {
    onCompleted: (data) => {
      if (data.generateQRCodes?.success) {
        toast.success(data.generateQRCodes.message);
        // Download only Excel (the button is labelled "Générer Excel")
        if (data.generateQRCodes.excelBase64 && data.generateQRCodes.excelFilename) {
          downloadExcel(data.generateQRCodes.excelBase64, data.generateQRCodes.excelFilename);
        }
        refetchLabels();
        refetchStats();
        onRefetch();
      } else {
        toast.error(data.generateQRCodes?.message || 'Erreur lors de la génération');
      }
      setGenerating(false);
    },
    onError: (err) => { toast.error(`Erreur: ${err.message}`); setGenerating(false); },
  });

  const [generateDraftLabelsExcel] = useMutation(GENERATE_DRAFT_LABELS_EXCEL, {
    onCompleted: (data) => {
      if (data.generateDraftLabelsExcel?.success) {
        toast.success(data.generateDraftLabelsExcel.message);
        if (data.generateDraftLabelsExcel.excelBase64 && data.generateDraftLabelsExcel.excelFilename) {
          downloadExcel(data.generateDraftLabelsExcel.excelBase64, data.generateDraftLabelsExcel.excelFilename);
        }
      } else {
        toast.error(data.generateDraftLabelsExcel?.message || 'Erreur lors de l\'export');
      }
      setExportingDraft(false);
    },
    onError: (err) => { toast.error(`Erreur: ${err.message}`); setExportingDraft(false); },
  });

  const [deleteDraftLabelsMutation] = useMutation(DELETE_DRAFT_LABELS, {
    onCompleted: (data) => {
      if (data.deleteDraftLabels?.success) {
        toast.success(data.deleteDraftLabels.message);
        refetchLabels();
        refetchStats();
        onRefetch();
      } else {
        toast.error(data.deleteDraftLabels?.message || 'Erreur lors de la suppression');
      }
      setDeletingDraft(false);
      setShowDeleteDraftConfirm(false);
    },
    onError: (err) => { toast.error(`Erreur: ${err.message}`); setDeletingDraft(false); },
  });

  const [confirmLabels, { loading: confirmingLabels }] = useMutation(CONFIRM_LABELS, {
    onCompleted: (data) => {
      if (data.confirmLabels?.success) {
        toast.success(`${data.confirmLabels.count} code(s) QR confirmé(s) et activé(s)`);
        refetchLabels();
        refetchStats();
        onRefetch();
      } else {
        toast.error(data.confirmLabels?.message || 'Erreur lors de la confirmation');
      }
    },
    onError: (err) => toast.error(`Erreur: ${err.message}`),
  });

  const labels   = labelsData?.labelsByPromotion || [];
  const stats    = statsData?.labelsStatistics;
  const total    = stats?.totalGenerated ?? labels.length;
  const scanned  = labels.filter(l => l.status === 'SCANNED').length;
  const active   = labels.filter(l => l.status === 'NOT_SCANNED').length;
  const draft    = labels.filter(l => l.status === 'EDITED').length;

  const filtered = labels.filter(l => {
    const matchSearch = !qrSearch ||
      l.code.toLowerCase().includes(qrSearch.toLowerCase()) ||
      (l.user && `${l.user.firstName} ${l.user.lastName}`.toLowerCase().includes(qrSearch.toLowerCase()));
    const matchFilter = qrFilter === 'all' || l.status === qrFilter;
    return matchSearch && matchFilter;
  });

  // Build generation history batches (labels created within 5 min = same batch)
  const batches = useMemo(() => {
    if (!labels.length) return [];
    const sorted = [...labels].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
    const result = [];
    sorted.forEach(l => {
      const t = new Date(l.createdAt).getTime();
      const last = result[result.length - 1];
      if (last && t - last.startTime < 5 * 60 * 1000) {
        last.count++;
        last.statuses.add(l.status);
      } else {
        result.push({ startTime: t, date: new Date(l.createdAt), count: 1, statuses: new Set([l.status]) });
      }
    });
    return result;
  }, [labels]);

  const handleGenerate = () => {
    if (!newQrQty || newQrQty <= 0) { toast.warning('Quantité invalide'); return; }
    const confirmedCount = labels.filter(l => l.status !== 'EDITED').length;
    setGenerating(true);
    generateQRCodes({ variables: { promotionId: promotion.id, quantity: confirmedCount + newQrQty } });
  };

  if (!promotion) return null;

  const getLabelStyle = (status) => {
    switch (status) {
      case 'SCANNED':     return { label: 'Scanné',     bg: 'bg-green-100',  text: 'text-green-800'  };
      case 'NOT_SCANNED': return { label: 'Non scanné', bg: 'bg-blue-100',   text: 'text-blue-800'   };
      case 'EDITED':      return { label: 'Brouillon',  bg: 'bg-orange-100', text: 'text-orange-800' };
      default:            return { label: status,       bg: 'bg-gray-100',   text: 'text-gray-800'   };
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] flex">
      <div className="flex-1 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="w-full max-w-xl bg-white h-full shadow-2xl flex flex-col overflow-hidden">

        {/* Header */}
        <div className="px-6 py-5 border-b bg-purple-50 border-purple-100">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <QrCode size={16} className="text-purple-600" />
                <span className="text-xs font-semibold text-purple-600 uppercase tracking-wide">Codes QR</span>
              </div>
              <h2 className="text-base font-bold text-gray-900 truncate">{promotion.name}</h2>
            </div>
            <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-purple-200 transition-colors flex-shrink-0">
              <X size={18} className="text-gray-500" />
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="px-4 sm:px-6 py-4 border-b border-gray-100 grid grid-cols-2 sm:grid-cols-4 gap-2">
          {[
            { label: 'Total',     value: total,   color: 'blue',   filter: 'all',         icon: QrCode      },
            { label: 'Actifs',    value: active,  color: 'green',  filter: 'NOT_SCANNED', icon: Eye         },
            { label: 'Brouillon', value: draft,   color: 'orange', filter: 'EDITED',      icon: Clock       },
            { label: 'Scannés',   value: scanned, color: 'purple', filter: 'SCANNED',     icon: CheckSquare },
          ].map(({ label, value, color, filter, icon: Icon }) => (
            <button
              key={filter}
              onClick={() => setQrFilter(qrFilter === filter ? 'all' : filter)}
              className={`p-2.5 rounded-xl border text-left transition-all ${
                qrFilter === filter
                  ? `bg-${color}-100 border-${color}-300`
                  : `bg-${color}-50 border-${color}-100 hover:bg-${color}-100`
              }`}
            >
              <Icon size={14} className={`text-${color}-600 mb-1`} />
              <p className={`text-lg font-bold text-${color}-800 leading-none`}>{value.toLocaleString()}</p>
              <p className={`text-xs text-${color}-600 mt-0.5`}>{label}</p>
            </button>
          ))}
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto">

          {/* Add QR codes */}
          <div className="px-6 pt-5 pb-4 border-b border-gray-100">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3 flex items-center gap-1.5">
              <Plus size={12} /> Ajouter des codes QR
            </p>
            {draft > 0 && (
              <div className="flex items-start gap-2 mb-3 px-3 py-2.5 bg-orange-50 border border-orange-200 rounded-lg">
                <AlertTriangle size={13} className="text-orange-500 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-orange-700">
                  Vous avez <strong>{draft}</strong> code{draft > 1 ? 's' : ''} en brouillon. Confirmez ou supprimez-les avant de générer de nouveaux codes.
                </p>
              </div>
            )}
            <div className="flex items-center gap-2">
              <input
                type="number"
                min="1"
                step="50"
                value={newQrQty}
                onChange={e => setNewQrQty(parseInt(e.target.value) || 50)}
                disabled={draft > 0}
                className="w-28 px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-400 disabled:opacity-40 disabled:cursor-not-allowed"
              />
              <span className="text-sm text-gray-600">nouveaux codes</span>
              <button
                onClick={handleGenerate}
                disabled={generating || draft > 0}
                title={draft > 0 ? 'Confirmez ou supprimez les codes en brouillon avant d\'en générer de nouveaux' : ''}
                className="ml-auto flex items-center gap-1.5 px-4 py-1.5 text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 rounded-lg disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {generating
                  ? <><div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" /> Génération…</>
                  : <><QrCode size={13} /> Générer Excel</>
                }
              </button>
            </div>
            {draft > 0 && (
              <div className="mt-2.5 space-y-1.5">
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => {
                      setExportingDraft(true);
                      generateDraftLabelsExcel({ variables: { promotionId: promotion.id } });
                    }}
                    disabled={exportingDraft}
                    className="flex items-center gap-1 px-2 py-1 text-xs font-semibold bg-gray-600 hover:bg-gray-700 text-white rounded-lg disabled:opacity-50 transition-colors"
                    title="Télécharger l'Excel des codes en brouillon"
                  >
                    {exportingDraft ? <RefreshCw size={11} className="animate-spin" /> : <Download size={11} />}
                    Excel brouillon ({draft})
                  </button>
                  <button
                    onClick={() => setShowDeleteDraftConfirm(true)}
                    className="flex items-center gap-1 px-2 py-1 text-xs font-semibold bg-red-100 hover:bg-red-200 text-red-700 border border-red-200 rounded-lg transition-colors"
                    title="Supprimer tous les codes QR en brouillon"
                  >
                    <Trash2 size={11} /> Supprimer ({draft})
                  </button>
                  <button
                    onClick={() => confirmLabels({ variables: { promotionId: promotion.id } })}
                    disabled={confirmingLabels}
                    className="flex-1 flex items-center justify-center gap-1 px-2 py-1 text-xs font-semibold bg-orange-500 hover:bg-orange-600 text-white rounded-lg disabled:opacity-50 transition-colors"
                  >
                    {confirmingLabels ? <RefreshCw size={11} className="animate-spin" /> : <CheckCircle size={11} />}
                    Confirmer le collage ({draft})
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Delete draft confirmation modal */}
          {showDeleteDraftConfirm && (
            <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-black/50">
              <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6">
                <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
                  <Trash2 size={22} className="text-red-600" />
                </div>
                <h3 className="text-base font-bold text-gray-900 text-center mb-2">Supprimer les codes en brouillon ?</h3>
                <p className="text-sm text-gray-500 text-center mb-6">
                  <strong>{draft}</strong> code{draft > 1 ? 's' : ''} QR en brouillon {draft > 1 ? 'seront définitivement supprimés' : 'sera définitivement supprimé'}.
                  Cette action est irréversible.
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowDeleteDraftConfirm(false)}
                    disabled={deletingDraft}
                    className="flex-1 px-4 py-2.5 text-sm font-medium border border-gray-200 text-gray-700 rounded-xl hover:bg-gray-50 disabled:opacity-50"
                  >
                    Annuler
                  </button>
                  <button
                    onClick={() => {
                      setDeletingDraft(true);
                      deleteDraftLabelsMutation({ variables: { promotionId: promotion.id } });
                    }}
                    disabled={deletingDraft}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-xl disabled:opacity-50"
                  >
                    {deletingDraft
                      ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      : <><Trash2 size={14} /> Supprimer</>
                    }
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Generation history */}
          {batches.length > 0 && (
            <div className="px-6 pt-4 pb-3 border-b border-gray-100">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                <History size={12} /> Historique des générations
              </p>
              <div className="space-y-1.5">
                {batches.map((batch, i) => {
                  const isDraft = batch.statuses.has('EDITED') && !batch.statuses.has('NOT_SCANNED') && !batch.statuses.has('SCANNED');
                  const isConf  = !batch.statuses.has('EDITED');
                  return (
                    <div key={i} className="flex items-center justify-between px-3 py-2 bg-gray-50 border border-gray-100 rounded-lg text-xs">
                      <div className="flex items-center gap-2 text-gray-600">
                        <QrCode size={11} className="text-gray-400" />
                        <span className="font-semibold text-gray-800">{batch.count} code{batch.count > 1 ? 's' : ''}</span>
                        <span className="text-gray-400">·</span>
                        <span>{format(batch.date, 'dd MMM yyyy HH:mm', { locale: fr })}</span>
                      </div>
                      <span className={`px-2 py-0.5 rounded-full font-medium ${isDraft ? 'bg-orange-100 text-orange-700' : isConf ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>
                        {isDraft ? 'Brouillon' : isConf ? 'Confirmé' : 'Mixte'}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Search */}
          <div className="px-6 py-3 border-b border-gray-100">
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              <input
                type="text"
                placeholder="Rechercher un code ou utilisateur…"
                value={qrSearch}
                onChange={e => setQrSearch(e.target.value)}
                className="w-full pl-8 pr-8 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-400"
              />
              {qrSearch && (
                <button onClick={() => setQrSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                  <X size={12} />
                </button>
              )}
            </div>
          </div>

          {/* List */}
          {labelsLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 size={24} className="animate-spin text-purple-500" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-16 text-gray-400">
              <QrCode size={36} className="mx-auto mb-2 opacity-30" />
              <p className="text-sm">Aucun code trouvé</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-50">
              {filtered.map(label => {
                const s       = getLabelStyle(label.status);
                const isDraft = label.status === 'EDITED';
                return (
                  <div key={label.id} className={`px-6 py-3 flex items-start gap-3 ${isDraft ? 'bg-orange-50/40' : 'hover:bg-gray-50'}`}>
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5 ${isDraft ? 'bg-orange-100' : 'bg-gray-100'}`}>
                      <QrCode size={14} className={isDraft ? 'text-orange-500' : 'text-gray-500'} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-mono font-medium text-gray-900 truncate">{label.code}</span>
                        <span className={`px-1.5 py-0.5 rounded text-xs font-medium flex-shrink-0 ${s.bg} ${s.text}`}>{s.label}</span>
                      </div>
                      {label.user && (
                        <p className="text-xs text-gray-500 mt-0.5 truncate">
                          {label.user.firstName} {label.user.lastName}
                          {label.pharmacy && ` · ${label.pharmacy.namePharmacy}`}
                        </p>
                      )}
                      {label.date && (
                        <p className="text-xs text-gray-400 mt-0.5">
                          {format(new Date(label.date), 'dd/MM/yyyy HH:mm', { locale: fr })}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button
                        onClick={() => { navigator.clipboard.writeText(label.code); toast.success('Code copié'); }}
                        className="p-1.5 rounded-lg hover:bg-gray-200 transition-colors text-gray-400 hover:text-gray-600"
                        title="Copier"
                      >
                        <Copy size={13} />
                      </button>
                      {label.image && (
                        <button
                          onClick={() => downloadQRImage(label)}
                          className="p-1.5 rounded-lg hover:bg-gray-200 transition-colors text-gray-400 hover:text-gray-600"
                          title="Télécharger"
                        >
                          <Download size={13} />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-gray-100 bg-gray-50">
          <p className="text-xs text-gray-500 text-center">
            {filtered.length.toLocaleString()} code{filtered.length !== 1 ? 's' : ''} affiché{filtered.length !== 1 ? 's' : ''}
            {qrFilter !== 'all' || qrSearch ? ` sur ${total.toLocaleString()} total` : ''}
          </p>
        </div>
      </div>
    </div>
  );
};

// ─── Promotion Detail Side Panel ──────────────────────────────────────────────

const PromotionDetailPanel = ({ promotion, onClose, onLabClick, onProductClick, onRefetch }) => {
  const [newEndDate, setNewEndDate]   = useState('');
  const [savingDate, setSavingDate]   = useState(false);
  const [localPromo, setLocalPromo]   = useState(promotion);

  const [updatePromotion] = useMutation(UPDATE_PROMOTION, {
    onError: (err) => toast.error(`Erreur: ${err.message}`),
  });

  if (!localPromo) return null;

  const active   = isActive(localPromo);
  const sc       = getPromoTag(localPromo);
  const start    = localPromo.startDate ? format(new Date(localPromo.startDate), 'dd MMM yyyy', { locale: fr }) : '—';
  const end      = localPromo.endDate   ? format(new Date(localPromo.endDate),   'dd MMM yyyy', { locale: fr }) : '—';
  const daysLeft = localPromo.endDate ? Math.ceil((new Date(localPromo.endDate) - new Date()) / 86400000) : null;
  const mazidDH  = getMazidDH(localPromo);

  const USER_TYPE_LABELS = { SELLER: 'Vendeur', PHARMACIST: 'Pharmacien', PARAPHARMACIST: 'Parapharmacien' };

  const handleExtend = async () => {
    if (!newEndDate) return;
    const current = new Date(localPromo.endDate);
    const chosen  = new Date(newEndDate);
    if (chosen <= current) { toast.warning('La nouvelle date doit être postérieure à la date de fin actuelle'); return; }
    setSavingDate(true);
    try {
      await updatePromotion({ variables: { updatePromotionInput: { id: localPromo.id, endDate: newEndDate } } });
      setLocalPromo(prev => ({ ...prev, endDate: newEndDate }));
      setNewEndDate('');
      toast.success('Date de fin mise à jour');
      onRefetch();
    } catch { /* handled by onError */ }
    finally { setSavingDate(false); }
  };

  return (
    <div className="fixed inset-0 z-[9999] flex">
      <div className="flex-1 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="w-full max-w-lg bg-white h-full shadow-2xl flex flex-col overflow-hidden">

        {/* Header */}
        <div className={`px-6 py-5 border-b ${active ? 'bg-green-50 border-green-100' : 'bg-gray-50 border-gray-100'}`}>
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${sc.bg} ${sc.text}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${sc.dot}`} />
                  {sc.label}
                </span>
                {localPromo.isSponsored && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                    <Star size={10} className="fill-amber-500 text-amber-500" /> Sponsorisée
                  </span>
                )}
              </div>
              <h2 className="text-lg font-bold text-gray-900 truncate">{localPromo.name}</h2>
              {localPromo.contrat && (
                <p className="text-xs text-gray-500 mt-0.5 flex items-center gap-1">
                  <FileText size={11} /> {localPromo.contrat.number} — {localPromo.contrat.nom}
                </p>
              )}
            </div>
            <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-200 transition-colors">
              <X size={18} className="text-gray-500" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">

          {/* Dates + Extend */}
          <section>
            <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Période</h3>
            <div className="flex items-center gap-3 p-4 bg-blue-50 rounded-xl border border-blue-100 mb-3">
              <Calendar size={20} className="text-blue-500 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium text-blue-900">{start} → {end}</p>
                {daysLeft > 0 ? (
                  <p className="text-xs text-blue-600 mt-0.5">{daysLeft} jour{daysLeft > 1 ? 's' : ''} restant{daysLeft > 1 ? 's' : ''}</p>
                ) : (
                  <p className="text-xs text-red-500 mt-0.5">Expirée</p>
                )}
              </div>
            </div>
            {/* Extend date */}
            <div className="p-3 bg-gray-50 rounded-xl border border-gray-100">
              <p className="text-xs font-semibold text-gray-500 mb-2">Prolonger la durée</p>
              <div className="flex items-center gap-2">
                <input
                  type="date"
                  value={newEndDate}
                  min={new Date(new Date(localPromo.endDate).getTime() + 86400000).toISOString().split('T')[0]}
                  onChange={e => setNewEndDate(e.target.value)}
                  className="flex-1 px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
                <button
                  onClick={handleExtend}
                  disabled={!newEndDate || savingDate}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <Save size={13} />
                  {savingDate ? '…' : 'Enregistrer'}
                </button>
              </div>
              <p className="text-xs text-gray-400 mt-1">Seulement une date postérieure à la date de fin actuelle.</p>
            </div>
          </section>

          {/* Laboratoire */}
          <section>
            <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Laboratoire</h3>
            <button
              onClick={() => onLabClick(localPromo.laboratory)}
              className="w-full flex items-center gap-3 p-3 bg-white border border-gray-200 rounded-xl hover:border-blue-300 hover:bg-blue-50 transition-all group text-left"
            >
              <div className="w-9 h-9 bg-indigo-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <Building2 size={16} className="text-indigo-600" />
              </div>
              <span className="flex-1 text-sm font-semibold text-gray-900">{localPromo.laboratory?.nom}</span>
              <ExternalLink size={14} className="text-gray-400 group-hover:text-blue-500 transition-colors" />
            </button>
          </section>

          {/* Produits */}
          <section>
            <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
              Produit{localPromo.products?.length > 1 ? 's' : ''} ({localPromo.products?.length || 0})
            </h3>
            <div className="space-y-2">
              {(localPromo.products || []).map(product => (
                <button
                  key={product.id}
                  onClick={() => onProductClick(product, localPromo.laboratory)}
                  className="w-full flex items-center gap-3 p-3 bg-white border border-gray-200 rounded-xl hover:border-blue-300 hover:bg-blue-50 transition-all group text-left"
                >
                  {product.image ? (
                    <img src={getProductImageUrl(product.image)} alt={product.name}
                      className="w-9 h-9 rounded-lg object-cover flex-shrink-0 bg-gray-100" />
                  ) : (
                    <div className="w-9 h-9 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Package size={16} className="text-gray-400" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-900 truncate">{product.name}</p>
                    <p className="text-xs text-gray-400">
                      {product.prixPGHT ? `PGHT: ${product.prixPGHT} DH` : product.price ? `${product.price} DH` : '—'}
                    </p>
                  </div>
                  <ExternalLink size={14} className="text-gray-400 group-hover:text-blue-500 transition-colors flex-shrink-0" />
                </button>
              ))}
            </div>
          </section>

          {/* Commissions */}
          <section>
            <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Commissions</h3>
            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 bg-green-50 rounded-xl border border-green-100">
                <p className="text-xs text-green-600 font-medium mb-1 flex items-center gap-1">
                  <DollarSign size={11} /> Vendeur
                </p>
                <p className="text-lg font-bold text-green-800">{localPromo.commissionAmount} DH</p>
              </div>
              <div className="p-3 bg-blue-50 rounded-xl border border-blue-100">
                <p className="text-xs text-blue-600 font-medium mb-1 flex items-center gap-1">
                  <Percent size={11} /> Mazid
                </p>
                {mazidDH ? (
                  <>
                    <p className="text-lg font-bold text-blue-800">{mazidDH} DH</p>
                    <p className="text-xs text-blue-500">({localPromo.commissionPercentage}% du PGHT)</p>
                  </>
                ) : (
                  <>
                    <p className="text-lg font-bold text-blue-800">{localPromo.commissionPercentage}%</p>
                    <p className="text-xs text-blue-500">du PGHT</p>
                  </>
                )}
              </div>
            </div>
          </section>

          {/* Destinataires */}
          {localPromo.allowedUserTypes?.length > 0 && (
            <section>
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Destinataires</h3>
              <div className="flex flex-wrap gap-2">
                {localPromo.allowedUserTypes.map(t => (
                  <span key={t} className="px-2.5 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-medium">
                    {USER_TYPE_LABELS[t] || t}
                  </span>
                ))}
              </div>
            </section>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 bg-gray-50">
          <button
            onClick={() => onLabClick(localPromo.laboratory)}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-indigo-600 text-white rounded-xl text-sm font-medium hover:bg-indigo-700 transition-colors"
          >
            <Building2 size={15} /> Voir la page du laboratoire
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Toggle Confirmation Modal ────────────────────────────────────────────────

const ToggleConfirmModal = ({ promotion, onConfirm, onCancel, loading }) => {
  const isDisabling = promotion.status === 'IN_PROGRESS';
  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6">
        <div className={`w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4 ${isDisabling ? 'bg-red-100' : 'bg-green-100'}`}>
          {isDisabling
            ? <PowerOff size={22} className="text-red-600" />
            : <Power size={22} className="text-green-600" />
          }
        </div>
        <h3 className="text-base font-bold text-gray-900 text-center mb-2">
          {isDisabling ? 'Désactiver la promotion ?' : 'Réactiver la promotion ?'}
        </h3>
        <p className="text-sm text-gray-500 text-center mb-1">
          <span className="font-semibold text-gray-800">{promotion.name}</span>
        </p>
        <p className="text-sm text-gray-500 text-center mb-6">
          {isDisabling
            ? 'La promotion passera en statut CLOSED et ne sera plus visible sur l\'application mobile.'
            : 'La promotion repassera en statut IN_PROGRESS et sera à nouveau visible sur l\'application mobile.'
          }
        </p>
        <div className="flex gap-3">
          <button
            onClick={onCancel}
            disabled={loading}
            className="flex-1 px-4 py-2.5 text-sm font-medium border border-gray-200 text-gray-700 rounded-xl hover:bg-gray-50 disabled:opacity-50"
          >
            Annuler
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-white rounded-xl disabled:opacity-50 disabled:cursor-not-allowed ${
              isDisabling ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'
            }`}
          >
            {loading
              ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              : isDisabling ? <><PowerOff size={14} /> Désactiver</> : <><Power size={14} /> Réactiver</>
            }
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────

const AdminPromotions = () => {
  const navigate = useNavigate();
  const [search, setSearch]       = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [selectedPromotion, setSelectedPromotion]     = useState(null);
  const [selectedProduct, setSelectedProduct]         = useState(null);
  const [selectedProductLab, setSelectedProductLab]   = useState(null);
  const [selectedQRPromotion, setSelectedQRPromotion] = useState(null);
  const [generatingFor, setGeneratingFor]             = useState(null);
  const [toggleConfirm, setToggleConfirm]             = useState(null); // promotion to toggle
  const [toggling, setToggling]                       = useState(false);

  const { data, loading, error, refetch } = useQuery(GET_ALL_PROMOTIONS, {
    fetchPolicy: 'cache-and-network',
  });

  const [updatePromotion] = useMutation(UPDATE_PROMOTION);

  const handleToggleStatus = async () => {
    if (!toggleConfirm) return;
    setToggling(true);
    const newStatus = toggleConfirm.status === 'IN_PROGRESS' ? 'CLOSED' : 'IN_PROGRESS';
    try {
      await updatePromotion({ variables: { updatePromotionInput: { id: toggleConfirm.id, status: newStatus } } });
      toast.success(newStatus === 'CLOSED' ? 'Promotion désactivée' : 'Promotion réactivée');
      refetch();
    } catch (err) {
      toast.error(`Erreur: ${err.message}`);
    } finally {
      setToggling(false);
      setToggleConfirm(null);
    }
  };

  const confirmDraftCollage = async (promo) => {
    try {
      await updatePromotion({ variables: { updatePromotionInput: { id: promo.id, status: 'IN_PROGRESS' } } });
      toast.success('Collage confirmé — promotion activée');
      refetch();
    } catch (err) {
      toast.error(`Erreur: ${err.message}`);
    }
  };

  const [generateQRCodes] = useMutation(GENERATE_QR_CODES, {
    onCompleted: (data) => {
      if (data.generateQRCodes?.success) {
        toast.success(data.generateQRCodes.message);
        if (data.generateQRCodes.pdfBase64 && data.generateQRCodes.filename) {
          downloadPDF(data.generateQRCodes.pdfBase64, data.generateQRCodes.filename);
        }
        if (data.generateQRCodes.excelBase64 && data.generateQRCodes.excelFilename) {
          downloadExcel(data.generateQRCodes.excelBase64, data.generateQRCodes.excelFilename);
        }
        refetch();
      } else {
        toast.error(data.generateQRCodes?.message || 'Erreur lors de la génération');
      }
      setGeneratingFor(null);
    },
    onError: (err) => { toast.error(`Erreur: ${err.message}`); setGeneratingFor(null); },
  });

  const promotions = data?.allPromotions || [];

  const stats = useMemo(() => ({
    total:     promotions.length,
    active:    promotions.filter(p => !isExpired(p) && p.status !== 'DRAFT').length,
    brouillon: promotions.filter(p => p.status === 'DRAFT' && !isExpired(p)).length,
    expired:   promotions.filter(isExpired).length,
  }), [promotions]);

  const filtered = useMemo(() => promotions.filter(p => {
    const matchSearch = !search ||
      p.name.toLowerCase().includes(search.toLowerCase()) ||
      p.laboratory?.nom?.toLowerCase().includes(search.toLowerCase()) ||
      p.products?.some(pr => pr.name.toLowerCase().includes(search.toLowerCase()));
    const matchStatus =
      statusFilter === 'all'       ||
      (statusFilter === 'active'    && !isExpired(p) && p.status !== 'DRAFT') ||
      (statusFilter === 'brouillon' && p.status === 'DRAFT' && !isExpired(p)) ||
      (statusFilter === 'expired'   && isExpired(p));
    return matchSearch && matchStatus;
  }), [promotions, search, statusFilter]);

  const handleLabClick = (lab) => { if (lab?.id) navigate(`/admin/laboratories/${lab.id}`); };

  const openProduct = (product, lab) => {
    setSelectedProduct(product);
    setSelectedProductLab(lab);
    setSelectedPromotion(null);
    setSelectedQRPromotion(null);
  };

  const openQRPanel = (promo) => {
    setSelectedQRPromotion(promo);
    setSelectedPromotion(null);
    setSelectedProduct(null);
  };

  const handleFirstGenerate = (promo, e) => {
    e.stopPropagation();
    if (!promo.quantity) { toast.warning('Quantité non définie pour cette promotion'); return; }
    setGeneratingFor(promo.id);
    generateQRCodes({ variables: { promotionId: promo.id, quantity: promo.quantity } });
  };

  return (
    <div className="p-6 space-y-6 h-full overflow-auto">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Tag className="text-indigo-600" size={24} /> Promotions
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">Toutes les promotions de la plateforme</p>
        </div>
        <button onClick={() => refetch()} className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors" title="Actualiser">
          <TrendingUp size={16} className="text-gray-500" />
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Total',        value: stats.total,     color: 'indigo', icon: Tag         },
          { label: 'Non expirées', value: stats.active,    color: 'green',  icon: CheckCircle },
          { label: 'Brouillon',    value: stats.brouillon, color: 'yellow', icon: Clock       },
          { label: 'Expirées',     value: stats.expired,   color: 'red',    icon: XCircle     },
        ].map(({ label, value, color, icon: Icon }) => (
          <div key={label} className={`bg-white rounded-xl border border-${color}-100 p-4 flex items-center gap-3`}>
            <div className={`w-10 h-10 bg-${color}-100 rounded-lg flex items-center justify-center flex-shrink-0`}>
              <Icon size={18} className={`text-${color}-600`} />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{value}</p>
              <p className="text-xs text-gray-500">{label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
          <input
            type="text"
            placeholder="Rechercher promotion, labo, produit…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-9 pr-9 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
          />
          {search && (
            <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
              <X size={14} />
            </button>
          )}
        </div>
        <div className="flex items-center gap-1.5 bg-white border border-gray-200 rounded-xl p-1">
          <Filter size={14} className="text-gray-400 ml-2" />
          {[
            { key: 'active',    label: 'Non expirées', count: stats.active    },
            { key: 'brouillon', label: 'Brouillon', count: stats.brouillon },
            { key: 'expired',   label: 'Expirées',  count: stats.expired   },
          ].map(opt => (
            <button
              key={opt.key}
              onClick={() => setStatusFilter(opt.key)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors flex items-center gap-1.5 ${
                statusFilter === opt.key ? 'bg-indigo-600 text-white' : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              {opt.label}
              <span className={`px-1.5 py-0.5 rounded-full text-xs font-bold ${
                statusFilter === opt.key ? 'bg-white/25 text-white' : 'bg-gray-200 text-gray-600'
              }`}>
                {opt.count}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      {loading && !data ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 size={28} className="animate-spin text-indigo-500" />
        </div>
      ) : error ? (
        <div className="flex items-center gap-3 p-4 bg-red-50 rounded-xl border border-red-200 text-red-700 text-sm">
          <AlertCircle size={18} /> Erreur lors du chargement des promotions.
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-20 text-gray-400">
          <Tag size={40} className="mx-auto mb-3 opacity-30" />
          <p className="text-sm font-medium">Aucune promotion trouvée</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-200 overflow-x-auto shadow-sm">
          <table className="min-w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Promotion</th>
                <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider hidden md:table-cell">Laboratoire</th>
                <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider hidden lg:table-cell">Produits</th>
                <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Période</th>
                <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider hidden sm:table-cell">Commission</th>
                <th className="px-5 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider hidden sm:table-cell">QR</th>
                <th className="px-3 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {filtered.map(promo => {
                const tag         = getPromoTag(promo);
                const expired     = isExpired(promo);
                const active      = isActive(promo);
                const startFmt    = format(new Date(promo.startDate), 'dd/MM/yy', { locale: fr });
                const endFmt      = format(new Date(promo.endDate),   'dd/MM/yy', { locale: fr });
                const daysLeft    = Math.ceil((new Date(promo.endDate) - new Date()) / 86400000);
                const mazidDH     = getMazidDH(promo);
                const qrCount     = promo.labelsCount ?? 0;
                const draftCount  = promo.draftLabelsCount ?? 0;
                const hasQR       = qrCount > 0;

                return (
                  <tr
                    key={promo.id}
                    onClick={() => setSelectedPromotion(promo)}
                    className={`cursor-pointer transition-colors hover:bg-indigo-50/40 ${
                      selectedPromotion?.id === promo.id ? 'bg-indigo-50' : ''
                    }`}
                  >
                    {/* Name + tag */}
                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-2.5">
                        <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${active ? 'bg-green-400 shadow-[0_0_6px_rgba(74,222,128,0.6)]' : tag.dot}`} />
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-gray-900 truncate max-w-[180px]">{promo.name}</p>
                          <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${tag.bg} ${tag.text} mt-0.5`}>
                            {tag.label}
                          </span>
                        </div>
                      </div>
                    </td>

                    {/* Lab */}
                    <td className="px-5 py-3.5 hidden md:table-cell">
                      <button
                        onClick={e => { e.stopPropagation(); handleLabClick(promo.laboratory); }}
                        className="flex items-center gap-1.5 text-sm font-medium text-indigo-700 hover:text-indigo-900 hover:underline"
                      >
                        <Building2 size={13} className="flex-shrink-0" />
                        <span className="truncate max-w-[120px]">{promo.laboratory?.nom || '—'}</span>
                      </button>
                    </td>

                    {/* Products */}
                    <td className="px-5 py-3.5 hidden lg:table-cell">
                      <div className="flex flex-wrap gap-1.5 max-w-[200px]">
                        {(promo.products || []).slice(0, 3).map(product => (
                          <button
                            key={product.id}
                            onClick={e => { e.stopPropagation(); openProduct(product, promo.laboratory); }}
                            className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 hover:bg-indigo-100 hover:text-indigo-800 text-gray-700 rounded-full text-xs font-medium transition-colors"
                          >
                            <Package size={10} />
                            <span className="truncate max-w-[90px]">{product.name}</span>
                          </button>
                        ))}
                        {promo.products?.length > 3 && (
                          <span className="px-2 py-0.5 bg-gray-100 text-gray-500 rounded-full text-xs">
                            +{promo.products.length - 3}
                          </span>
                        )}
                      </div>
                    </td>

                    {/* Dates */}
                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-1.5 text-xs text-gray-600">
                        <Calendar size={12} className="text-gray-400 flex-shrink-0" />
                        <span className="whitespace-nowrap">{startFmt} → {endFmt}</span>
                      </div>
                      {expired ? (
                        <p className="text-xs text-red-400 mt-0.5 pl-4">Expirée</p>
                      ) : daysLeft > 0 ? (
                        <p className="text-xs text-green-600 mt-0.5 pl-4">{daysLeft}j restants</p>
                      ) : null}
                    </td>

                    {/* Commission */}
                    <td className="px-5 py-3.5 hidden sm:table-cell">
                      <div className="text-xs space-y-0.5">
                        <p className="text-green-700 font-medium">
                          +{promo.commissionAmount} DH <span className="text-gray-400 font-normal">vendeur</span>
                        </p>
                        <p className="text-blue-700 font-medium">
                          {mazidDH
                            ? <>{mazidDH} DH <span className="text-gray-400 font-normal">({promo.commissionPercentage}%)</span> <span className="text-gray-400 font-normal">Mazid</span></>
                            : <>{promo.commissionPercentage}% <span className="text-gray-400 font-normal">Mazid</span></>
                          }
                        </p>
                      </div>
                    </td>

                    {/* QR */}
                    <td className="px-5 py-3.5 text-center hidden sm:table-cell">
                      {hasQR ? (
                        <div className="flex flex-col items-center gap-0.5">
                          <button
                            onClick={e => { e.stopPropagation(); openQRPanel(promo); }}
                            className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-purple-100 hover:bg-purple-200 text-purple-800 border border-purple-200 rounded-full text-xs font-semibold transition-colors"
                          >
                            <QrCode size={11} /> {qrCount.toLocaleString()}
                          </button>
                          {draftCount > 0 ? (
                            <button
                              onClick={e => { e.stopPropagation(); openQRPanel(promo); }}
                              className="inline-flex items-center gap-0.5 text-xs text-orange-600 font-medium hover:underline"
                              title={`${draftCount} code(s) en brouillon`}
                            >
                              <Clock size={10} /> {draftCount} brouillon{draftCount > 1 ? 's' : ''}
                            </button>
                          ) : (
                            <span className="inline-flex items-center gap-0.5 text-xs text-green-600 font-medium">
                              <CheckCircle size={10} /> générés
                            </span>
                          )}
                        </div>
                      ) : promo.quantity ? (
                        <div className="flex flex-col items-center gap-1">
                          <span className="text-xs text-gray-400">{promo.quantity.toLocaleString()} prévus</span>
                          {generatingFor === promo.id ? (
                            <Loader2 size={14} className="animate-spin text-purple-500" />
                          ) : (
                            <button
                              onClick={e => handleFirstGenerate(promo, e)}
                              className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium text-white bg-purple-600 hover:bg-purple-700 rounded-full transition-colors"
                            >
                              <QrCode size={10} /> Générer
                            </button>
                          )}
                        </div>
                      ) : (
                        <span className="text-xs text-gray-300">—</span>
                      )}
                    </td>

                    {/* Actions */}
                    <td className="px-3 py-3.5" onClick={e => e.stopPropagation()}>
                      <div className="flex items-center gap-1">
                        {!expired && promo.status === 'DRAFT' && (
                          <button
                            onClick={e => { e.stopPropagation(); confirmDraftCollage(promo); }}
                            title="Confirmer le collage (BROUILLON → EN COURS)"
                            className="p-1.5 rounded-lg text-green-500 hover:text-green-700 hover:bg-green-50 transition-colors"
                          >
                            <CheckCircle size={15} />
                          </button>
                        )}
                        {!expired && (promo.status === 'IN_PROGRESS' || promo.status === 'CLOSED') && (
                          <button
                            onClick={e => { e.stopPropagation(); setToggleConfirm(promo); }}
                            title={promo.status === 'IN_PROGRESS' ? 'Désactiver' : 'Réactiver'}
                            className={`p-1.5 rounded-lg transition-colors ${
                              promo.status === 'IN_PROGRESS'
                                ? 'text-red-400 hover:text-red-600 hover:bg-red-50'
                                : 'text-green-500 hover:text-green-700 hover:bg-green-50'
                            }`}
                          >
                            {promo.status === 'IN_PROGRESS' ? <PowerOff size={15} /> : <Power size={15} />}
                          </button>
                        )}
                        <ChevronRight size={16} className="text-gray-300" />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Promotion detail side panel */}
      {selectedPromotion && (
        <PromotionDetailPanel
          promotion={selectedPromotion}
          onClose={() => setSelectedPromotion(null)}
          onLabClick={lab => { setSelectedPromotion(null); handleLabClick(lab); }}
          onProductClick={(product, lab) => openProduct(product, lab)}
          onRefetch={refetch}
        />
      )}

      {/* Product detail side panel */}
      {selectedProduct && (
        <ProductDetailPanel
          product={selectedProduct}
          lab={selectedProductLab}
          onClose={() => { setSelectedProduct(null); setSelectedProductLab(null); }}
          onProductUpdated={() => refetch()}
        />
      )}

      {/* QR detail side panel */}
      {selectedQRPromotion && (
        <QRDetailPanel
          promotion={selectedQRPromotion}
          onClose={() => setSelectedQRPromotion(null)}
          onRefetch={refetch}
        />
      )}

      {/* Toggle status confirmation modal */}
      {toggleConfirm && (
        <ToggleConfirmModal
          promotion={toggleConfirm}
          loading={toggling}
          onConfirm={handleToggleStatus}
          onCancel={() => setToggleConfirm(null)}
        />
      )}
    </div>
  );
};

export default AdminPromotions;
